import { useState } from 'react';
import {
  AccordionItemContainer,
  ArrowIcon,
  Description,
  StyledAccordionItem,
  Title,
  TitleContainer,
  CookiesDescription,
} from './styledComponents';
import Link from 'next/link';

type AccordionItemProps = {
  title: string;
  description: string;
  isOpen: boolean;
  onClick: () => void;
  linkInListText?: string;
  linkInListUrl?: string;
  contactUsText?: string;
  cookies?: string[];
};

export type ListItem = { title: string; description: string };

type AccordionProps = {
  list: ListItem[];
  linkInListText?: string;
  linkInListUrl?: string;
  contactUsText?: string;
  cookies?: string[];
};

const AccordionItem = ({
  title,
  description,
  onClick,
  isOpen,
  linkInListText,
  linkInListUrl,
  contactUsText,
  cookies,
}: AccordionItemProps) => {
  const contactUsDescription = description.split(/(contact-us-identifier)/);
  const beforeContactUs = contactUsDescription[0];
  const afterContactUs = contactUsDescription[2] ?? description;

  const splitDescription = afterContactUs.split(/(link-in-list-identifier)/);
  const beforeLink = splitDescription[0];
  const afterLink = splitDescription[2];

  const cookiesDescription = description.split(/(cookies-identifier)/);
  const beforeCookies = cookiesDescription[0];
  const afterCookies = cookiesDescription[2] ?? description;

  const showComplexDescription =
    description.includes('contact-us-identifier') ||
    description.includes('link-in-list-identifier') ||
    description.includes('cookies-identifier');

  return (
    <AccordionItemContainer onClick={onClick}>
      <TitleContainer>
        <ArrowIcon iconName={isOpen ? 'arrow-up' : 'arrow-down'} />
        <Title>{title}</Title>
      </TitleContainer>
      {isOpen && (
        <>
          {cookies && showComplexDescription ? (
            <CookiesDescription>
              <p>{beforeCookies}</p>
              <ul>
                {cookies.map((item: string) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
              <p>{afterCookies}</p>
            </CookiesDescription>
          ) : (
            <Description>
              {showComplexDescription ? (
                <>
                  {description.includes('contact-us-identifier') && (
                    <>
                      {beforeContactUs}
                      <Link href="/help/contact-us">{contactUsText}</Link>
                    </>
                  )}
                  {beforeLink}
                  <a
                    href={linkInListUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {linkInListText}
                  </a>
                  {afterLink}
                </>
              ) : (
                <>{description}</>
              )}
            </Description>
          )}
        </>
      )}
    </AccordionItemContainer>
  );
};

const Accordion = ({
  list,
  linkInListText,
  linkInListUrl,
  contactUsText,
  cookies,
}: AccordionProps) => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const handleItemClick = (index: number) => {
    if (index === openIndex) {
      setOpenIndex(null);
    } else {
      setOpenIndex(index);
    }
  };

  return (
    <>
      {list.map((item, index) => (
        <StyledAccordionItem key={`accordion-el-${item.title}`}>
          <AccordionItem
            title={item.title}
            description={item.description}
            isOpen={openIndex === index}
            onClick={() => handleItemClick(index)}
            linkInListText={linkInListText}
            linkInListUrl={linkInListUrl}
            contactUsText={contactUsText}
            cookies={cookies}
          />
          <hr />
        </StyledAccordionItem>
      ))}
    </>
  );
};

export default Accordion;
